import moment from 'moment';
import { format0 } from 'utils/number';
import { getDateString } from 'utils/dates';
import { HTTPError } from 'ky';

/**
 * ky's `api` carries a prefixUrl, which rejects a leading slash — and every href the
 * backend hands out has one.
 * @param {string} href The backend's href.
 * @return {string} It, without the leading slash.
 */
export const apiPath = (href: string): string => href.replace(/^\//, '');

// Carries the reasons a workspace read or write was refused, so the caller shows the
// backend's own wording rather than ky's generic HTTP text.
//
// `code` and `status` ride along for the few callers that must ROUTE on which refusal it
// was rather than merely print it — the Add/Rebuild Wellbore sheet keeps itself open for a
// refusal about the selection and hands the rest to the card. Both are optional: the shared
// events endpoint answers with bare strings and has no code, and a non-HTTP failure has no
// status. Nothing that only renders `reasons` has to change.
/** Everything a refusal carries beyond its reasons. An object, so the list can grow. */
export interface RefusalDetails {
  /** Its code, where the envelope was the coded kind. */
  code?: string;
  /** The HTTP status. */
  status?: number;
  /** The slots a stale-stick refusal names. */
  staleSlotIds?: number[];
  /** Which manual-add row a batch refusal is about — see `rowFrom`. */
  row?: number;
}

export class WorkspaceRefused extends Error {
  // Declared and assigned rather than written as constructor parameter properties: the
  // project compiles with `erasableSyntaxOnly`, which allows only type syntax TypeScript can
  // strip, and a parameter property emits a real assignment.
  readonly reasons: string[];
  readonly code?: string;
  readonly status?: number;
  readonly staleSlotIds?: number[];
  /** Which manual-add row a batch refusal is about — see `rowFrom`. */
  readonly row?: number;

  /**
   * @param {string[]} reasons The backend's own wording for the refusal.
   * @param {RefusalDetails} [details] Whatever else the envelope carried.
   */
  constructor(reasons: string[], details: RefusalDetails = {}) {
    super('Workspace request refused');
    this.reasons = reasons;
    this.code = details.code;
    this.status = details.status;
    this.staleSlotIds = details.staleSlotIds;
    this.row = details.row;
  }
}

// The two error envelopes this feature sees: `/workspaces` answers with coded objects,
// the shared events endpoint with plain strings. Reading both means a real reason
// surfaces instead of the generic fallback.
interface CodedError {
  code?: string;
  message?: string;
  details?: {
    incomplete?: string[];
    /**
     * The LTD slots an `INVALID_LTD_SLOTS` refusal is actually about.
     *
     * Already camelCase on the wire — it is written that way in the Ruby hash, not
     * produced by jbuilder's key format, so it is NOT `stale_slot_ids`.
     */
    staleSlotIds?: number[];
    /**
     * The manual-add batch row a validation refusal is about, as an index into the `manual`
     * array that was SENT — not into the sheet's full row list, which includes rows the
     * reader unchecked. Also already camelCase-safe: it is a bare `row` in the Ruby hash.
     */
    row?: number;
  };
}
interface ErrorPayload {
  errors?: (CodedError | string)[];
}

/**
 * The first entry's code, where the envelope is the coded kind. Only the first: a refusal
 * is one decision, and the rest of the array elaborates it.
 * @param {ErrorPayload | null} body The parsed error body.
 * @return {string | undefined} The code, if there is one.
 */
const codeFrom = (body: ErrorPayload | null): string | undefined => {
  const first = (body?.errors ?? [])[0];

  return first && typeof first !== 'string' ? first.code : undefined;
};

/**
 * The slots a stale-stick refusal names, so the picker can prune exactly those.
 * @param {ErrorPayload | null} body The parsed error envelope.
 * @return {number[] | undefined} The named slot ids, or undefined if none were named.
 */
const staleSlotIdsFrom = (body: ErrorPayload | null): number[] | undefined => {
  const first = (body?.errors ?? [])[0];
  if (!first || typeof first === 'string') return undefined;
  const ids = first.details?.staleSlotIds;

  return Array.isArray(ids) && ids.length > 0 ? ids : undefined;
};

/**
 * The manual-add row a batch refusal names, so the sheet can pin the offending row rather
 * than reporting a batch-wide failure over a table the reader has to re-read.
 * @param {ErrorPayload | null} body The parsed error envelope.
 * @return {number | undefined} The row index, or undefined when none was named.
 */
const rowFrom = (body: ErrorPayload | null): number | undefined => {
  const first = (body?.errors ?? [])[0];
  if (!first || typeof first === 'string') return undefined;
  const row = first.details?.row;

  return typeof row === 'number' ? row : undefined;
};

/**
 * The reasons worth showing, preferring a refusal's itemised details over its message.
 * @param {ErrorPayload | null} body The parsed error body.
 * @return {string[]} The reasons.
 */
const reasonsFrom = (body: ErrorPayload | null): string[] => {
  const raw = body?.errors ?? [];
  const first = raw[0];

  // A refusal's `details.incomplete` beats its generic message: a NOT_PROMOTABLE
  // answer names the blocking items there while the message alone says only that the
  // workspace is not promotable.
  if (first && typeof first !== 'string' && first.details?.incomplete?.length) {
    return first.details.incomplete;
  }

  return raw
    .map((entry) => (typeof entry === 'string' ? entry : (entry.message ?? '')))
    .filter((reason): reason is string => !!reason);
};

/**
 * Turns a failed request into its reasons, so React Query's `error` carries something
 * worth rendering.
 * @param {unknown} error Whatever the request threw.
 * @return {Promise<Error>} A WorkspaceRefused where the body gave reasons, else an Error.
 */
export const toWorkspaceError = async (error: unknown): Promise<Error> => {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error : new Error('Request failed');
  }

  const body = (await error.response
    .json()
    .catch(() => null)) as ErrorPayload | null;

  return new WorkspaceRefused(reasonsFrom(body), {
    code: codeFrom(body),
    status: error.response.status,
    staleSlotIds: staleSlotIdsFrom(body),
    row: rowFrom(body),
  });
};

/**
 * What to say about a refused request: the backend's own wording where it gave one,
 * otherwise a line that adds something. The caller has already named the action, and a
 * 500 answers with an HTML page carrying no reason at all.
 * @param {unknown} error The query or mutation's error, or null.
 * @param {string} fallback What to say when the backend gave no reason.
 * @return {string | null} The message, or null when there was no error.
 */
export const readWriteFailure = (
  error: unknown,
  fallback: string
): string | null => {
  if (!error) {
    return null;
  }
  if (error instanceof WorkspaceRefused && error.reasons.length > 0) {
    return error.reasons.join(' ');
  }
  return fallback;
};

/** The subtitle for a refusal that came with no reason of its own. */
export const UNEXPLAINED_FAILURE =
  'The server did not say why. Try again, and check the logs if it keeps failing.';

// The name a workspace is born with when none is given, after the backend's own
// (`WellWorkspace.default_name`): "Well Workspace — 3 wells — Sep 11, 6:58 PM". Built here
// too so the create modal can offer it before the request — ready to keep or replace.
// `MMM D, h:mm A` is the strftime `%b %-d, %-I:%M %p` the backend uses. The count carries
// thousands separators ("4,725 wells"), as every other count on the page does; the modal
// always sends the name, so this is the one that gets saved.
/**
 * @param {number} wellCount How many wells the workspace covers.
 * @param {Date} at When it is being created.
 * @return {string} The default name, matching WellWorkspace.default_name.
 */
export const defaultWorkspaceName = (wellCount: number, at: Date): string =>
  `Well Workspace — ${format0(wellCount)} ${
    wellCount === 1 ? 'well' : 'wells'
  } — ${moment(at).format('MMM D, h:mm A')}`;

/**
 * A Well Workspace's URL. Every opener navigates to it, so the address bar names the
 * workspace and the link can be shared.
 * @param {number | string} id The workspace's id.
 * @return {string} The path.
 */
export const wellWorkspacePath = (id: number | string): string =>
  `/well_workspaces/${id}`;

/**
 * What an opener hands over in router state alongside the path: the well the reader
 * came from, so the workspace lands on it rather than on its first well. State rather
 * than a query param, so the URL stays just the workspace's.
 */
export interface WellWorkspaceLocationState {
  well?: string | null;
  /** True when the reader arrives from creating this workspace, so the page can say so. */
  created?: boolean;
}

/**
 * What an unnamed, newly authored well is called until somebody names it.
 *
 * Story 1 creates exactly one blank well per workspace, so it needs no number. The mock's
 * "New Well 1 / New Well 2" numbering belongs with Story 3, where several can be added at
 * once — and the wording itself is still an open product question.
 */
export const NEW_WELL_PLACEHOLDER = 'New well';

/**
 * How a staged well is labelled in the rail, the tree and the card headers.
 *
 * The backend already falls back to the uwi for an EXISTING well with no derived name, so a
 * null name means the well was authored here and has not been named — never "unknown". It
 * deliberately does not surface the synthetic backend uwi, which would read as a name the
 * well does not have and contradict the promote gate.
 * @param {object} well The staged well, or anything carrying its `name`.
 * @return {string} The label to show.
 */
export const wellDisplayName = (well: { name: string | null }): string => {
  return well.name ?? NEW_WELL_PLACEHOLDER;
};

/** What a date that does not parse says. Worded as the input, not as the form. */
export const DATE_INVALID_TEXT = 'Enter a date as MM/DD/YYYY.';

/**
 * A typed date, normalized to the ISO form the endpoint takes.
 *
 * Returns `null` for text that is not a date yet, which is what HOLDS the editor open —
 * the server parses strictly and 422s a typo rather than coercing it, so catching it here
 * is the difference between a correction and a failed request. An empty field is a clear,
 * not a parse failure, so it returns `''`.
 * @param {string} input The raw text.
 * @return {string | null} `YYYY-MM-DD`, `''` to clear, or null when it is not a date.
 */
export const parseTypedDate = (input: string): string | null => {
  const value = input.trim();
  if (value === '') return '';

  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
  if (!match) return null;

  const [, month, day, year] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  // Round-tripping catches the overflow `new Date` performs silently — 02/31/2021 would
  // otherwise land on March 3rd, which is not the date anyone typed.
  if (
    parsed.getFullYear() !== Number(year) ||
    parsed.getMonth() !== Number(month) - 1 ||
    parsed.getDate() !== Number(day)
  ) {
    return null;
  }

  return getDateString(parsed);
};
